import { PaymentData } from '../../types';
import { extractTextFromPDF } from '../services/pdf';
import { extractInvoiceData } from '../services/openai/extractors';
import { sendInvoicesToAPI } from '../services/invoice/api';
import Cookies from 'js-cookie';

export interface ProcessedInvoice {
  id: string;
  file: File;
  data: PaymentData;
  qrPosition: { x: number; y: number } | null;
  status: 'processing' | 'success' | 'error';
  error?: string;
}

export async function processInvoiceFile(file: File): Promise<ProcessedInvoice> {
  try {
    // Extract text from PDF
    const text = await extractTextFromPDF(file);
    
    // Use AI to extract data
    const data = await extractInvoiceData(text);
    
    // Validate extracted data
    if (!data.name || !data.iban || !data.amount) {
      return {
        id: crypto.randomUUID(),
        file,
        data,
        qrPosition: null,
        status: 'error',
        error: 'Could not extract required payment information'
      };
    }
    
    return {
      id: crypto.randomUUID(),
      file,
      data: {
        ...data,
        currency: data.currency || 'EUR'
      },
      qrPosition: { x: 50, y: 85 },
      status: 'success'
    };
  } catch (error) {
    console.error('Failed to process invoice:', error);
    return {
      id: crypto.randomUUID(),
      file,
      data: {
        name: '',
        iban: '',
        amount: 0,
        currency: 'EUR'
      },
      qrPosition: null,
      status: 'error',
      error: error instanceof Error ? error.message : 'Failed to process invoice'
    };
  }
}

export async function processInvoices(
  files: File[],
  onProgress?: (current: number, total: number) => void,
  onQuotaUpdate?: (quota: number) => void
): Promise<ProcessedInvoice[]> {
  // Check authentication first
  const authUuid = Cookies.get('auth_uuid');
  if (!authUuid) {
    console.error('User not authenticated');
    throw new Error('Authentication required');
  }

  const results: ProcessedInvoice[] = [];
  const total = files.length;
  
  try {
    // Process all files
    for (let i = 0; i < total; i++) {
      onProgress?.(i + 1, total);
      const result = await processInvoiceFile(files[i]);
      results.push(result);

      // Send successful invoice to API immediately after processing
      if (result.status === 'success') {
        console.log('Sending successful invoice to API immediately:', result.id);
        await sendInvoicesToAPI([result], onQuotaUpdate);
      }
    }

    return results;
  } catch (error) {
    console.error('Error in invoice processing:', error);
    throw error;
  }
}